import { useEffect, useState } from "react";
import { useGetIdentity, useNotify } from "ra-core";
import { Factory, Plus, Search, Edit2, Trash2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getSupabaseClient } from "../providers/supabase/supabase";

type Supplier = {
  id: number;
  name: string;
  name_zh: string | null;
  country: string | null;
  city: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  main_categories: string[] | null;
  certifications: string[] | null;
  rating_quality: number | null;
  rating_delivery: number | null;
  rating_service: number | null;
  status: string;
  payment_terms: string | null;
  notes: string | null;
  created_at: string;
};

const STATUS_BADGE: Record<string, string> = {
  active: "bg-green-100 text-green-700",
  suspended: "bg-yellow-100 text-yellow-700",
  blacklist: "bg-red-100 text-red-700",
};

const StarRating = ({ value }: { value: number | null }) => (
  <div className="flex gap-0.5">
    {[1,2,3,4,5].map(i => (
      <Star key={i} className={`w-3 h-3 ${i <= (value ?? 0) ? "fill-yellow-400 text-yellow-400" : "text-gray-200"}`} />
    ))}
  </div>
);

const emptyForm = () => ({
  name: "", name_zh: "", country: "", city: "", address: "",
  license_number: "", contact_name: "", contact_email: "", contact_phone: "",
  main_categories: "", certifications: "", monthly_capacity: "",
  payment_terms: "", rating_quality: "0", rating_delivery: "0", rating_service: "0",
  status: "active", notes: "",
});

const SuppliersPage = () => {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = () => {
    getSupabaseClient()
      .from("suppliers")
      .select("*")
      .order("created_at", { ascending: false })
      .then(({ data }) => { setSuppliers(data ?? []); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (s: Supplier) => {
    setEditing(s);
    setForm({
      name: s.name, name_zh: s.name_zh ?? "", country: s.country ?? "",
      city: s.city ?? "", address: "", license_number: "",
      contact_name: s.contact_name ?? "", contact_email: s.contact_email ?? "",
      contact_phone: s.contact_phone ?? "",
      main_categories: (s.main_categories ?? []).join(", "),
      certifications: (s.certifications ?? []).join(", "),
      monthly_capacity: "", payment_terms: s.payment_terms ?? "",
      rating_quality: (s.rating_quality ?? 0).toString(),
      rating_delivery: (s.rating_delivery ?? 0).toString(),
      rating_service: (s.rating_service ?? 0).toString(),
      status: s.status, notes: s.notes ?? "",
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { notify("Supplier name is required", { type: "error" }); return; }
    setSaving(true);
    const supabase = getSupabaseClient();
    const payload = {
      name: form.name.trim(),
      name_zh: form.name_zh || null,
      country: form.country || null,
      city: form.city || null,
      contact_name: form.contact_name || null,
      contact_email: form.contact_email || null,
      contact_phone: form.contact_phone || null,
      main_categories: form.main_categories ? form.main_categories.split(",").map(s => s.trim()).filter(Boolean) : null,
      certifications: form.certifications ? form.certifications.split(",").map(s => s.trim()).filter(Boolean) : null,
      payment_terms: form.payment_terms || null,
      rating_quality: parseInt(form.rating_quality) || null,
      rating_delivery: parseInt(form.rating_delivery) || null,
      rating_service: parseInt(form.rating_service) || null,
      status: form.status,
      notes: form.notes || null,
      updated_at: new Date().toISOString(),
    };
    if (editing) {
      const { error } = await supabase.from("suppliers").update(payload).eq("id", editing.id);
      if (error) { notify(error.message, { type: "error" }); setSaving(false); return; }
      notify("Supplier updated", { type: "info" });
    } else {
      const { error } = await supabase.from("suppliers").insert({ ...payload, created_by: identity?.id ?? null });
      if (error) { notify(error.message, { type: "error" }); setSaving(false); return; }
      notify("Supplier created", { type: "info" });
    }
    setSaving(false); setShowForm(false); load();
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this supplier?")) return;
    await getSupabaseClient().from("suppliers").delete().eq("id", id);
    notify("Deleted", { type: "info" }); load();
  };

  const filtered = suppliers.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.name.toLowerCase().includes(q) || (s.name_zh ?? "").includes(q) || (s.country ?? "").toLowerCase().includes(q);
  });

  const avgRating = (s: Supplier) => {
    const vals = [s.rating_quality, s.rating_delivery, s.rating_service].filter(v => v != null) as number[];
    return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
  };

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Factory className="w-6 h-6" /> Suppliers / Factories</h1>
        <Button size="sm" onClick={openCreate} className="gap-1"><Plus className="w-3.5 h-3.5" /> Add Supplier</Button>
      </div>

      <div className="relative max-w-xs">
        <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
        <Input className="pl-8 h-8 text-sm" placeholder="Name, country..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Factory className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>No suppliers yet. <button onClick={openCreate} className="text-primary underline">Add your first factory</button></p>
        </div>
      ) : (
        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-xs text-muted-foreground uppercase">
                <th className="text-left px-4 py-2.5">Factory</th>
                <th className="text-left px-4 py-2.5 hidden md:table-cell">Country</th>
                <th className="text-left px-4 py-2.5 hidden lg:table-cell">Contact</th>
                <th className="text-left px-4 py-2.5 hidden lg:table-cell">Certifications</th>
                <th className="text-center px-4 py-2.5">Rating</th>
                <th className="text-center px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id} className="border-t hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium">{s.name}</div>
                    {s.name_zh && <div className="text-xs text-muted-foreground">{s.name_zh}</div>}
                    {s.main_categories?.length ? (
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {s.main_categories.slice(0, 3).map((c, i) => (
                          <span key={i} className="text-xs bg-muted px-1.5 rounded">{c}</span>
                        ))}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{[s.city, s.country].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <div>{s.contact_name ?? "—"}</div>
                    {s.contact_email && <div className="text-xs text-muted-foreground">{s.contact_email}</div>}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {s.certifications?.length ? (
                      <div className="flex gap-1 flex-wrap">
                        {s.certifications.slice(0, 3).map((c, i) => <Badge key={i} variant="outline" className="text-xs py-0">{c}</Badge>)}
                      </div>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="text-sm font-semibold">{avgRating(s) ?? "—"}</div>
                    <StarRating value={avgRating(s) ? Math.round(parseFloat(avgRating(s)!)) : null} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_BADGE[s.status] ?? ""}`}>{s.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => openEdit(s)} className="p-1 text-muted-foreground hover:text-foreground"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(s.id)} className="p-1 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={o => !o && setShowForm(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Supplier" : "New Supplier"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Factory Name *</Label>
              <Input className="h-8 text-sm" value={form.name} onChange={e => set("name", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>中文名称</Label>
              <Input className="h-8 text-sm" value={form.name_zh} onChange={e => set("name_zh", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Country</Label>
              <Input className="h-8 text-sm" placeholder="China" value={form.country} onChange={e => set("country", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>City</Label>
              <Input className="h-8 text-sm" placeholder="Guangzhou" value={form.city} onChange={e => set("city", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Contact Name</Label>
              <Input className="h-8 text-sm" value={form.contact_name} onChange={e => set("contact_name", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Contact Email</Label>
              <Input className="h-8 text-sm" type="email" value={form.contact_email} onChange={e => set("contact_email", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Contact Phone</Label>
              <Input className="h-8 text-sm" value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Payment Terms</Label>
              <Input className="h-8 text-sm" placeholder="30% deposit, 70% on delivery" value={form.payment_terms} onChange={e => set("payment_terms", e.target.value)} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Main Categories (comma-separated)</Label>
              <Input className="h-8 text-sm" placeholder="Knitwear, Woven, Denim" value={form.main_categories} onChange={e => set("main_categories", e.target.value)} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Certifications (comma-separated)</Label>
              <Input className="h-8 text-sm" placeholder="ISO 9001, BSCI, WRAP" value={form.certifications} onChange={e => set("certifications", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Quality Rating (1-5)</Label>
              <Select value={form.rating_quality} onValueChange={v => set("rating_quality", v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>{[0,1,2,3,4,5].map(n => <SelectItem key={n} value={n.toString()}>{n === 0 ? "Not rated" : `${n} stars`}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Delivery Rating (1-5)</Label>
              <Select value={form.rating_delivery} onValueChange={v => set("rating_delivery", v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>{[0,1,2,3,4,5].map(n => <SelectItem key={n} value={n.toString()}>{n === 0 ? "Not rated" : `${n} stars`}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => set("status", v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="blacklist">Blacklist</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Textarea className="text-sm resize-none" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : (editing ? "Update" : "Create")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuppliersPage;
